/**
 * THE CHANGE HISTORY, ONE CARD TO A CHANGE: what changed, whether she was told, the messages it
 * sent, and the forms it still needs.
 */
import {
  CHARGE_DECISION_LABELS,
  SILENCE_REASON_LABELS,
  VISIT_CHANGE_KIND_LABELS,
  humanise,
} from '@fk/shared';
import type { ChangeRow, ContactRow, Me } from '../../api.ts';
import { Card, Detail, Pill } from '../../components/AppShell.tsx';
import { NotToldPill } from '../../components/StatusPills.tsx';
import { formatInstant, longDay } from '../../format.ts';
import { MESSAGE_CHANNEL_LABELS } from '../../labels.ts';
import { ChargeForm, SilenceForm } from './ChangeForms.tsx';
import { PAIR } from './shared.ts';

export function ChangeCard({
  change,
  me,
  contacts,
  onReload,
  resolvable = true,
}: {
  change: ChangeRow;
  me: Me;
  contacts: readonly ContactRow[];
  onReload: () => void;
  /**
   * False for the change the panel above is already offering the way out of. One change with two
   * of the same form is two of the same field id, and a label that points at both of them.
   */
  resolvable?: boolean;
}) {
  const pending = change.toldAt === null && change.silenceReason === null;
  const isCancellation = change.kind.startsWith('cancelled');

  return (
    <Card className={pending ? 'border-coral/40!' : ''}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-serif text-lg leading-tight">
          {VISIT_CHANGE_KIND_LABELS[change.kind]}
        </h3>
        {change.toldAt ? (
          <Pill tone="sage">Told</Pill>
        ) : change.silenceReason ? (
          <Pill>{SILENCE_REASON_LABELS[change.silenceReason]}</Pill>
        ) : (
          <NotToldPill />
        )}
        {change.inside24h ? <Pill tone="gold">Under a day's notice</Pill> : null}
      </div>

      <dl className={`mt-2 ${PAIR}`}>
        <Detail label="From" value={change.fromDate ? longDay(change.fromDate) : null} />
        <Detail label="To" value={change.toDate ? longDay(change.toDate) : null} />
        <Detail label="Told" value={formatInstant(change.toldAt)} />
        <Detail
          label="Recorded"
          value={`${formatInstant(change.createdAt) ?? change.createdAt} by ${
            // The API sends the author's id and no name. An id means nothing to a reader, so the
            // author is "you" or "the office" until the route can say who; nothing is invented.
            change.changedBy === me.userId ? 'you' : change.changedBy ? 'the office' : 'the system'
          }`}
        />
        {isCancellation && change.chargeDecision !== 'not_decided' ? (
          <Detail label="Charge" value={CHARGE_DECISION_LABELS[change.chargeDecision]} />
        ) : null}
      </dl>
      <dl>
        <Detail label="Note" value={change.note} />
      </dl>

      {change.intents.length > 0 ? (
        <ul className="mt-2 space-y-2">
          {change.intents.map((intent) => {
            const contact = contacts.find((row) => row.id === intent.contactId);
            return (
              <li key={intent.id} className="rounded-lg border border-navy/10 bg-cream px-3 py-2">
                <p className="text-sm whitespace-pre-line">{intent.body}</p>
                <p className="mt-1 text-xs text-navy/65">
                  {MESSAGE_CHANNEL_LABELS[intent.channel]}
                  {contact ? ` to ${contact.displayName}` : ''},{' '}
                  {humanise(intent.state).toLowerCase()}
                  {intent.handedOffAt ? `, handed off ${formatInstant(intent.handedOffAt)}` : ''}
                </p>
              </li>
            );
          })}
        </ul>
      ) : null}

      {pending && resolvable ? <SilenceForm changeId={change.id} onDone={onReload} /> : null}
      {isCancellation && change.chargeDecision === 'not_decided' ? (
        <ChargeForm changeId={change.id} onDone={onReload} />
      ) : null}
    </Card>
  );
}
